import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { gl } from '../../Engine';

/**
 * {@link SignedDistanceField} is just a 3D {@link WebGLTexture texture}.
 */
export class SignedDistanceField {
    /**
     * {@link WebGLTexture}-object that stores the textureData on the GPU.
     */
    private _texture: WebGLTexture;

    /**
     * Current {@link vec3 size} of this {@link SignedDistanceField} and it's {@link _texture texture} in pixels.
     */
    private _size: vec3;

    public constructor(size: ReadonlyVec3) {
        this._size = vec3.create();
        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Texture creation for SDF failed`);
        }
        this._texture = texture;
        this.resize(size);
    }

    /**
     * Method to get the {@link _size size} of this {@link SignedDistanceField} in pixels.
     */
    public get size(): ReadonlyVec3 {
        return this._size;
    }

    /**
     * Method to get reference to the {@link WebGLTexture} stored within this {@link SignedDistanceField}.
     */
    public get texture(): WebGLTexture {
        return this._texture;
    }

    /**
     * Method to resize this {@link SignedDistanceField}.\
     * This checks if the old {@link _size size} equals the {@link size}. If it does, returns false.\
     * Otherwise the {@link _texture texture} is resized and True is returned.
     * @param size The new {@link _size size} that the {@link SignedDistanceField} should have in pixels.
     * @returns Returns true if the {@link SignedDistanceField} was resized,\
     * false if {@link SignedDistanceField}'s {@link _size size} was already equal to {@link size}, in which case resize was not needed.
     */
    public resize(size: ReadonlyVec3): boolean {
        if (vec3.equals(this._size, size)) {
            return false;
        }
        vec3.copy(this._size, size);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);
        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST);
        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(
            gl.TEXTURE_3D,
            gl.TEXTURE_MIN_FILTER,
            gl.LINEAR_MIPMAP_LINEAR
        );
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
        gl.texImage3D(
            gl.TEXTURE_3D,
            0, // level
            gl.R8, // internalformat
            size[0], // width
            size[1], // height
            size[2], // depth
            0,
            gl.RED, // format
            gl.UNSIGNED_BYTE, // type
            null // pixel
        );
        return true;
    }

    /**
     * Method to get the SDF texture values from WebGLTexture as an array.\
     */
    public toArray(): Uint8Array[] {
        const resultArray = new Array<Uint8Array>();

        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        for (let z = 0; z < this._size[2]; ++z) {
            resultArray.push(this.sliceToArrayInternal(z));
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
        return resultArray;
    }

    // get the SDF distance value at a given point, this indicates the distance to the edge of the contour
    // distance is not in physical units, but as the distance field value
    public distanceToContour(point: ReadonlyVec3): number {
        if (
            point[0] < 0 ||
            point[0] >= this._size[0] ||
            point[1] < 0 ||
            point[1] >= this._size[1] ||
            point[2] < 0 ||
            point[2] >= this._size[2]
        ) {
            return 9999;
        }
        // the indices of the surrounding grid points
        const x0 = Math.floor(point[0]);
        const y0 = Math.floor(point[1]);
        const z0 = Math.floor(point[2]);
        const x1 = Math.min(x0 + 1, this._size[0] - 1);
        const y1 = Math.min(y0 + 1, this._size[1] - 1);
        const z1 = Math.min(z0 + 1, this._size[2] - 1);

        // Calculate the fractional part in each dimension
        const dx = point[0] - x0;
        const dy = point[1] - y0;
        const dz = point[2] - z0;
        const pixels = new Uint8Array(4);

        const getPixel = (x: number, y: number, z: number) => {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                this._texture,
                0,
                z
            );
            gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
            return pixels[0];
        };

        // get the distance values at the 8 surrounding grid points
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

        const v000 = getPixel(x0, y0, z0);
        const v100 = getPixel(x1, y0, z0);
        const v010 = getPixel(x0, y1, z0);
        const v110 = getPixel(x1, y1, z0);
        const v001 = getPixel(x0, y0, z1);
        const v101 = getPixel(x1, y0, z1);
        const v011 = getPixel(x0, y1, z1);
        const v111 = getPixel(x1, y1, z1);

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);

        // Perform trilinear interpolation
        const v00 = v000 * (1 - dx) + v100 * dx;
        const v01 = v001 * (1 - dx) + v101 * dx;
        const v10 = v010 * (1 - dx) + v110 * dx;
        const v11 = v011 * (1 - dx) + v111 * dx;

        const v0 = v00 * (1 - dy) + v10 * dy;
        const v1 = v01 * (1 - dy) + v11 * dy;

        const v = v0 * (1 - dz) + v1 * dz;
        return Math.abs(v - 127.5);
    }

    public sliceToArray(sliceIdx: number): Uint8Array {
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        const result = this.sliceToArrayInternal(sliceIdx);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
        return result;
    }

    private sliceToArrayInternal(sliceIdx: number): Uint8Array {
        const bytesPerPixel = 4;
        const xySize = this._size[0] * this._size[1];

        const sliceArray = new Uint8Array(xySize);
        const slicePixels = new Uint8Array(bytesPerPixel * xySize);
        let resultPixelIdx = 0;
        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            this._texture,
            0,
            sliceIdx
        );

        gl.readPixels(
            0,
            0,
            this.size[0],
            this.size[1],
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            slicePixels
        );
        for (
            let pixelIdx = 0;
            pixelIdx < xySize * bytesPerPixel;
            pixelIdx += bytesPerPixel
        ) {
            const r = slicePixels[pixelIdx];
            sliceArray[resultPixelIdx++] = r;
        }
        return sliceArray;
    }

    public arrayToTextureSlice(array: Uint8Array, sliceIdx: number): void {
        if (array.length !== this.size[0] * this.size[1]) {
            throw new Error(
                `Array length ${array.length} does not match texture size ${this.size[0]} x ${this.size[1]}`
            );
        }
        const newSizeX =
            this.size[0] % 4 === 0
                ? this.size[0]
                : this.size[0] + (4 - (this.size[0] % 4));

        // By experimentation, it was discovered that WebGL requires the texture data to be aligned to 4 bytes
        // when we copy a sub region to the texture using texSubImage3D().
        if (newSizeX !== this.size[0]) {
            const tmpArray = new Uint8Array(newSizeX * this.size[1]);
            for (let y = 0; y < this.size[1]; ++y) {
                for (let x = 0; x < this.size[0]; ++x) {
                    tmpArray[y * newSizeX + x] = array[y * this.size[0] + x];
                }
            }
            array = tmpArray;
        }
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        gl.texSubImage3D(
            gl.TEXTURE_3D,
            0,
            0,
            0,
            sliceIdx,
            this.size[0],
            this.size[1],
            1,
            gl.RED,
            gl.UNSIGNED_BYTE,
            array
        );
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    public arrayToTexture(array: Uint8Array[], sliceOffset: number): void {
        for (let z = 0; z < array.length; ++z) {
            this.arrayToTextureSlice(array[z], z + sliceOffset);
        }
    }

    /**
     * Method to {@link gl.bindTexture bind} the {@link _texture texture} to {@link gl.TEXTURE_3D TEXTURE_3D} target.
     * @returns True to indicate that the {@link _texture texture} was successfully bound.
     */
    public bind(): boolean {
        gl.bindTexture(gl.TEXTURE_3D, this._texture);
        return true;
    }

    /**
     * Method to unbind the texture. Will unbind any texture that has been bound.
     */
    public unbind() {
        gl.bindTexture(gl.TEXTURE_3D, null);
    }

    /**
     * Method to delete this {@link SignedDistanceField}.\
     * This deletes the {@link _texture texture}, de-allocating the WebGL memory consumed by this {@link SignedDistanceField}.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully deleted.
     */
    public delete() {
        gl.deleteTexture(this._texture);
    }

    /**
     * Method to clear the {@link _texture texture} of this {@link SignedDistanceField}.\
     * This clears the entire {@link _texture texture} to black.
     */
    public clearTexture(): void {
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        Array.from({ length: this.size[2] }).forEach((_, z) => {
            gl.framebufferTextureLayer(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                this._texture,
                0,
                z
            );
            gl.clear(gl.COLOR_BUFFER_BIT);
        });
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /**
     * Method to clear a single slice of the {@link _texture texture} of this {@link SignedDistanceField}.\
     * @param contourSliceZ The index of the slice to clear.
     */
    public clearSlice(contourSliceZ: number): void {
        const fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.framebufferTextureLayer(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            this._texture,
            0,
            contourSliceZ
        );
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.deleteFramebuffer(fb);
    }

    /**
     * Method to get all the slices of this {@link SignedDistanceField}.
     */
    public getAllSlices(): number[] {
        return Array.from({ length: this.size[2] }, (_, i) => i);
    }
}
